import type { NeutralActivityDto } from "@normax-performance/shared-types";
import type { NeutralRowState } from "../daily-entry.types";
import { SearchCombobox, Input, type ComboboxOption } from "../../../shared-ui/primitives";
import type { RowFocusNav } from "./EntryGrid";
import "./NeutralActivityRow.css";

export interface NeutralActivityRowProps {
  index: number;
  row: NeutralRowState;
  neutralActivities: NeutralActivityDto[];
  onChange: (patch: Partial<NeutralRowState>) => void;
  onDelete: () => void;
  readOnly?: boolean;
  /**
   * Enter-to-next-field registry (task:
   * docs/NORMAX_PERFORMANCE_DAILY_ENTRY_ENTER_KEYBOARD_FLOW_FORENSIC_REPORT.md
   * §9) — real field order: 0 Vrsta, 1 Vreme (min), 2 Broj radnog naloga,
   * 3 Napomena. `undefined` in read-only rows.
   */
  focusNav?: RowFocusNav;
  rowRef?: React.Ref<HTMLDivElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
}

/**
 * Visually and structurally separate from NORMED_WORK rows — no
 * Proizvod/Planirano/% columns (DAILY_ENTRY_INTERACTION_SPEC.md §8.7).
 */
export function NeutralActivityRow({
  index,
  row,
  neutralActivities,
  onChange,
  onDelete,
  readOnly,
  focusNav,
  rowRef,
  onKeyDown,
}: NeutralActivityRowProps) {
  const options: ComboboxOption[] = neutralActivities.map((a) => ({ id: a.id, label: a.canonicalName }));

  return (
    <div className="nx-neutral-row" data-row-id={row.localId} ref={rowRef} onKeyDown={onKeyDown}>
      <span className="nx-neutral-row__index tabular-nums">#{index}</span>
      <span className="nx-neutral-row__tag">Neutralna aktivnost</span>
      {readOnly ? (
        <span className="nx-neutral-row__ro">{options.find((o) => o.id === row.neutralActivityId)?.label}</span>
      ) : (
        <div className="nx-neutral-row__field">
          <SearchCombobox
            label="Vrsta"
            items={options}
            selectedKey={row.neutralActivityId}
            onSelectionChange={(id) => onChange({ neutralActivityId: id })}
            required
            inputRef={focusNav?.field(0).ref}
            onKeyDown={focusNav?.field(0).onKeyDown}
          />
        </div>
      )}
      <div className="nx-neutral-row__field nx-neutral-row__field--num">
        <Input
          label="Vreme (min)"
          inputMode="decimal"
          value={row.durationMinutes}
          disabled={readOnly}
          onChange={(e) => onChange({ durationMinutes: e.target.value })}
          ref={focusNav?.field(1).ref}
          onKeyDown={focusNav?.field(1).onKeyDown}
        />
      </div>
      <div className="nx-neutral-row__field">
        <Input
          label="Broj radnog naloga"
          value={row.workOrderReference ?? ""}
          disabled={readOnly}
          onChange={(e) => onChange({ workOrderReference: e.target.value })}
          ref={focusNav?.field(2).ref}
          onKeyDown={focusNav?.field(2).onKeyDown}
        />
      </div>
      <div className="nx-neutral-row__field">
        <Input
          label="Napomena"
          value={row.note ?? ""}
          disabled={readOnly}
          onChange={(e) => onChange({ note: e.target.value })}
          ref={focusNav?.field(3).ref}
          onKeyDown={focusNav?.field(3).onKeyDown}
        />
      </div>
      {!readOnly ? (
        <button type="button" className="nx-neutral-row__delete" onClick={onDelete} aria-label="Obriši red">
          🗑
        </button>
      ) : null}
    </div>
  );
}
